import { Controller } from "@hotwired/stimulus"
import { createSelect2Manager } from "../lib/select2_manager"

const USER_PICK_EVENT = "change.mcpGrantDirty"

// Connects to data-controller="mcp-grant-form"
//
// Admin "New MCP Access Grant" form (epic #7053): initialises the select2
// AJAX user picker against the admin users search endpoint, restoring any
// preselected user after a validation-failure re-render. Inside the New Grant
// modal the dropdown is appended to the modal (dropdownParent) — appended to
// <body> it would sit under the modal's focus trap and backdrop.
export default class extends Controller {
  static targets = ["userSelect"]

  connect() {
    if (!this.hasUserSelectTarget) return

    this.select2Manager = createSelect2Manager()
    this.select2Manager.init(
      this.userSelectTarget,
      (element) => this.select2Options(element.dataset, element.closest(".modal")),
      {
        onInitialized: (element, $element) => {
          this.restorePreselection(element.dataset, $element)
          return this.announceUserPicks(element, $element)
        }
      }
    )
  }

  // BUTTON-9: Select2 reports a pick through jQuery's `change` only, which a
  // native listener never hears — the modal's card-dirty would keep Create
  // disabled after a user is chosen. Re-announce each pick as a native,
  // bubbling `input`. Wired after the preselection restore, so a re-rendered
  // form's own restore is not reported as an edit. Returns the cleanup.
  announceUserPicks(element, $element) {
    const announce = () => element.dispatchEvent(new Event("input", { bubbles: true }))
    $element.on(USER_PICK_EVENT, announce)
    return () => $element.off(USER_PICK_EVENT, announce)
  }

  disconnect() {
    this.select2Manager?.destroyAll()
  }

  select2Options(dataset, modal = null) {
    return {
      dropdownParent: modal ? window.$(modal) : undefined,
      placeholder: dataset.select2Placeholder || "Search for user by name or email...",
      minimumInputLength: Number(dataset.select2MinimumInputLength || 2),
      width: dataset.select2Width || "100%",
      allowClear: dataset.select2AllowClear === "true",
      ajax: {
        url: dataset.select2AjaxUrl,
        dataType: "json",
        delay: 250,
        data: (params) => ({ q: params.term }),
        processResults: (data) => ({ results: data }),
        cache: true
      }
    }
  }

  restorePreselection(dataset, $element) {
    const { preselectedId, preselectedText } = dataset
    if (!preselectedId || !preselectedText) return

    const option = new Option(preselectedText, preselectedId, true, true)
    $element.append(option).trigger("change")
  }
}
